class BeekissDeliveryDatePicker extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.valueInput = this.querySelector('[data-delivery-date-value]');
    this.trigger = this.querySelector('[data-delivery-date-trigger]');
    this.label = this.querySelector('[data-delivery-date-label]');
    this.calendar = this.querySelector('[data-delivery-date-calendar]');
    this.error = this.querySelector('[data-delivery-date-error]');
    this.required = this.dataset.required === 'true';
    this.disableWeekends = this.dataset.disableWeekends !== 'false';
    this.leadBusinessDays = Number(this.dataset.leadBusinessDays || 0);
    this.maxDays = Number(this.dataset.maxDays || 180);
    this.blockedDates = this.parseBlockedDates(this.dataset.blackoutDates || '');
    this.minDate = this.addBusinessDays(this.today(), this.leadBusinessDays);
    this.maxDate = this.addDays(this.today(), this.maxDays);

    const savedDate = this.dateFromValue(this.valueInput.value);
    if (savedDate && !this.isUnavailable(savedDate)) {
      this.selectedDate = savedDate;
      this.valueInput.value = this.toValue(savedDate);
      this.label.textContent = this.formatDate(savedDate);
    } else {
      this.valueInput.value = '';
    }

    const firstVisibleDate = this.selectedDate || this.minDate;
    this.viewYear = firstVisibleDate.getFullYear();
    this.viewMonth = firstVisibleDate.getMonth();

    this.trigger.addEventListener('click', () => this.toggle());
    this.calendar.addEventListener('click', (event) => this.handleCalendarClick(event));
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });

    const form = document.getElementById(this.dataset.formId);
    if (form) {
      form.addEventListener('submit', (event) => {
        if (!this.validate()) {
          event.preventDefault();
          this.open();
        }
      });
    }
  }

  today() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  dateFromValue(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return this.toValue(date) === value ? date : null;
  }

  toValue(date) {
    const pad = (number) => String(number).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  formatDate(date) {
    const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日（${weekdays[date.getDay()]}）`;
  }

  addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  addBusinessDays(date, days) {
    let result = new Date(date);
    let added = 0;
    while (added < days) {
      result = this.addDays(result, 1);
      if (!this.isWeekend(result) && !this.blockedDates.has(this.toValue(result))) added += 1;
    }
    return result;
  }

  parseBlockedDates(rawDates) {
    const dates = new Set();
    rawDates.split(/[\n,]+/).map((item) => item.trim()).filter(Boolean).forEach((item) => {
      const [startValue, endValue = startValue] = item.split('..').map((value) => value.trim());
      const start = this.dateFromValue(startValue);
      const end = this.dateFromValue(endValue);
      if (!start || !end || end < start) return;

      let date = new Date(start);
      while (date <= end) {
        dates.add(this.toValue(date));
        date = this.addDays(date, 1);
      }
    });
    return dates;
  }

  isWeekend(date) {
    return date.getDay() === 0 || date.getDay() === 6;
  }

  isUnavailable(date) {
    const value = this.toValue(date);
    return date < this.minDate
      || date > this.maxDate
      || (this.disableWeekends && this.isWeekend(date))
      || this.blockedDates.has(value);
  }

  toggle() {
    if (this.calendar.hidden) this.open();
    else this.close();
  }

  open() {
    this.render();
    this.calendar.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.calendar.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  render() {
    const firstDay = new Date(this.viewYear, this.viewMonth, 1);
    const lastDay = new Date(this.viewYear, this.viewMonth + 1, 0);
    const firstAllowedMonth = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1);
    const lastAllowedMonth = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), 1);
    const previousMonth = new Date(this.viewYear, this.viewMonth - 1, 1);
    const nextMonth = new Date(this.viewYear, this.viewMonth + 1, 1);
    const weekdayLabels = ['日', '月', '火', '水', '木', '金', '土'];
    const days = [];

    for (let blank = 0; blank < firstDay.getDay(); blank += 1) {
      days.push('<span class="beekiss-delivery-date__day beekiss-delivery-date__day--blank" aria-hidden="true"></span>');
    }

    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      const date = new Date(this.viewYear, this.viewMonth, day);
      const value = this.toValue(date);
      const unavailable = this.isUnavailable(date);
      const selected = this.selectedDate && value === this.toValue(this.selectedDate);
      days.push(`<button type="button" class="beekiss-delivery-date__day${selected ? ' is-selected' : ''}" data-delivery-date="${value}"${unavailable ? ' disabled' : ''} aria-label="${this.formatDate(date)}${unavailable ? ' は選択できません' : ''}">${day}</button>`);
    }

    this.calendar.innerHTML = `
      <div class="beekiss-delivery-date__calendar-header">
        <button type="button" class="beekiss-delivery-date__month-button" data-month-change="-1" aria-label="前の月"${previousMonth < firstAllowedMonth ? ' disabled' : ''}>‹</button>
        <strong>${this.viewYear}年${this.viewMonth + 1}月</strong>
        <button type="button" class="beekiss-delivery-date__month-button" data-month-change="1" aria-label="次の月"${nextMonth > lastAllowedMonth ? ' disabled' : ''}>›</button>
      </div>
      <div class="beekiss-delivery-date__weekdays">${weekdayLabels.map((day) => `<span>${day}</span>`).join('')}</div>
      <div class="beekiss-delivery-date__days">${days.join('')}</div>
    `;
  }

  handleCalendarClick(event) {
    const monthButton = event.target.closest('[data-month-change]');
    if (monthButton) {
      this.viewMonth += Number(monthButton.dataset.monthChange);
      if (this.viewMonth < 0) {
        this.viewMonth = 11;
        this.viewYear -= 1;
      }
      if (this.viewMonth > 11) {
        this.viewMonth = 0;
        this.viewYear += 1;
      }
      this.render();
      return;
    }

    const dateButton = event.target.closest('[data-delivery-date]');
    if (!dateButton || dateButton.disabled) return;
    const date = this.dateFromValue(dateButton.dataset.deliveryDate);
    if (!date || this.isUnavailable(date)) return;

    this.selectedDate = date;
    this.valueInput.value = this.toValue(date);
    this.label.textContent = this.formatDate(date);
    this.persistValue(this.valueInput.value);
    this.clearError();
    this.close();
  }

  persistValue(value) {
    if (!window.routes || !window.routes.cart_update_url) return;
    fetch(window.routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({ attributes: { 'お届け希望日': value } }),
    }).catch(() => {
      // The checkout form still submits the selected value if a background save fails.
    });
  }

  validate() {
    const date = this.dateFromValue(this.valueInput.value);
    if (!date && this.required) {
      this.setError('お届け希望日を選択してください。');
      return false;
    }
    if (date && this.isUnavailable(date)) {
      this.setError('選択された日は配送を承れません。別の日を選択してください。');
      return false;
    }
    this.clearError();
    return true;
  }

  setError(message) {
    this.error.textContent = message;
    this.error.hidden = false;
    this.trigger.setAttribute('aria-invalid', 'true');
  }

  clearError() {
    this.error.textContent = '';
    this.error.hidden = true;
    this.trigger.removeAttribute('aria-invalid');
  }
}

if (!customElements.get('delivery-date-picker')) {
  customElements.define('delivery-date-picker', BeekissDeliveryDatePicker);
}
